import Phaser from "phaser";
import { PALETTE_NUM } from "../themes/palette.js";

const LOCKED_CELL_TEXTURE_PREFIX = "__moduleGridLockedCell";
const LOCKED_LAYER_TEXTURE_PREFIX = "__moduleGridLockedLayer";
const EMPTY_LAYER_TEXTURE_PREFIX = "__moduleGridEmptyLayer";

let lockedLayerTextureSeq = 0;
let emptyLayerTextureSeq = 0;

export interface ModuleGridLockedLayer {
  readonly image: Phaser.GameObjects.Image;
  readonly texture: Phaser.Textures.CanvasTexture;
}

export interface ModuleGridLockedLayerCell {
  readonly x: number;
  readonly y: number;
  readonly stateKey: string;
  readonly canUnlock: boolean;
  readonly strokeColor: number;
}

export interface ModuleGridEmptyLayer {
  readonly image: Phaser.GameObjects.Image;
  readonly texture: Phaser.Textures.CanvasTexture;
}

export interface ModuleGridEmptyLayerCell {
  readonly x: number;
  readonly y: number;
  readonly stateKey: string;
  readonly fillColor: number;
  readonly strokeColor: number;
  readonly strokeWidth: number;
  readonly alpha: number;
  readonly railColor: number;
  readonly railAlpha: number;
}

export function createModuleGridLockedLayer(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
): ModuleGridLockedLayer | undefined {
  const textures = scene.textures;
  if (!textures?.createCanvas || !textures.getFrame || !textures.getFrame("icon_grid_lock")) return undefined;
  const texture = textures.createCanvas(`${LOCKED_LAYER_TEXTURE_PREFIX}_${lockedLayerTextureSeq++}_${width}x${height}`, width, height);
  if (!texture) return undefined;
  const image = scene.add
    .image(x, y, texture.key)
    .setName("moduleGridLockedLayer")
    .setOrigin(0, 0)
    .setDisplaySize(width, height)
    .setDepth(2)
    .setVisible(false)
    .setActive(false);
  return { image, texture };
}

export function createModuleGridEmptyLayer(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
): ModuleGridEmptyLayer | undefined {
  const textures = scene.textures;
  if (!textures?.createCanvas) return undefined;
  const texture = textures.createCanvas(`${EMPTY_LAYER_TEXTURE_PREFIX}_${emptyLayerTextureSeq++}_${width}x${height}`, width, height);
  if (!texture) return undefined;
  const image = scene.add
    .image(x, y, texture.key)
    .setName("moduleGridEmptyLayer")
    .setOrigin(0, 0)
    .setDisplaySize(width, height)
    .setDepth(2)
    .setVisible(false)
    .setActive(false);
  return { image, texture };
}

export function drawModuleGridLockedCell(
  ctx: CanvasRenderingContext2D,
  lockFrame: Phaser.Textures.Frame,
  x: number,
  y: number,
  size: number,
  canUnlock: boolean,
  strokeColor: number,
): boolean {
  ctx.save();
  ctx.globalAlpha = canUnlock ? 0.62 : 0.5;
  ctx.fillStyle = colorToCss(PALETTE_NUM.gridCellEmpty);
  ctx.fillRect(x, y, size, size);
  if (canUnlock) {
    ctx.strokeStyle = colorToCss(strokeColor);
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, size - 1, size - 1);
  }
  ctx.restore();

  const lockSize = 16;
  return drawTintedFrame(
    ctx,
    lockFrame,
    x + (size - lockSize) / 2,
    y + (size - lockSize) / 2 - 6,
    lockSize,
    lockSize,
    canUnlock ? PALETTE_NUM.textBright : PALETTE_NUM.textMuted,
    canUnlock ? 0.82 : 0.42,
  );
}

export function drawModuleGridEmptyCell(
  ctx: CanvasRenderingContext2D,
  cell: ModuleGridEmptyLayerCell,
  size: number,
): void {
  const x = cell.x;
  const y = cell.y;
  ctx.save();
  ctx.globalAlpha = cell.alpha;
  ctx.fillStyle = colorToCss(cell.fillColor);
  ctx.fillRect(x, y, size, size);
  if (cell.strokeWidth > 0) {
    ctx.strokeStyle = colorToCss(cell.strokeColor);
    ctx.lineWidth = cell.strokeWidth;
    const inset = cell.strokeWidth / 2;
    ctx.strokeRect(x + inset, y + inset, Math.max(1, size - cell.strokeWidth), Math.max(1, size - cell.strokeWidth));
  }
  ctx.restore();

  const cx = x + size / 2;
  const cy = y + size / 2;
  const rail = size / 2 - 7;
  const gap = Math.max(4, size * 0.13);
  ctx.save();
  ctx.strokeStyle = colorToCss(cell.railColor);
  ctx.lineWidth = 1;
  ctx.globalAlpha = cell.railAlpha;
  ctx.beginPath();
  ctx.moveTo(cx - rail, cy);
  ctx.lineTo(cx - gap, cy);
  ctx.moveTo(cx + gap, cy);
  ctx.lineTo(cx + rail, cy);
  ctx.moveTo(cx, cy - rail);
  ctx.lineTo(cx, cy - gap);
  ctx.moveTo(cx, cy + gap);
  ctx.lineTo(cx, cy + rail);
  ctx.stroke();
  ctx.globalAlpha = cell.railAlpha * 0.75;
  ctx.fillStyle = colorToCss(cell.railColor);
  ctx.beginPath();
  ctx.arc(cx, cy, 1.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export function ensureModuleGridLockedCellTexture(
  scene: Phaser.Scene,
  size: number,
  stateKey: string,
  canUnlock: boolean,
  strokeColor: number,
): string | undefined {
  const textures = scene.textures;
  if (!textures?.getFrame || !textures.createCanvas || !textures.exists) return undefined;
  const key = lockedCellTextureKey(size, stateKey, canUnlock, PALETTE_NUM.gridCellEmpty, strokeColor, PALETTE_NUM.textMuted);
  if (textures.exists(key)) return key;

  const textureSize = Math.max(1, Math.floor(size));
  const texture = textures.createCanvas(key, textureSize, textureSize);
  const lockFrame = textures.getFrame("icon_grid_lock");
  if (!texture || !lockFrame) {
    if (texture) textures.remove?.(key);
    return undefined;
  }

  const ctx = texture.getContext();
  ctx.clearRect(0, 0, textureSize, textureSize);
  const ok = drawModuleGridLockedCell(ctx, lockFrame, 0, 0, textureSize, canUnlock, strokeColor);
  if (!ok) {
    textures.remove?.(key);
    return undefined;
  }
  texture.refresh();
  return key;
}

function lockedCellTextureKey(
  size: number,
  stateKey: string,
  canUnlock: boolean,
  bgColor: number,
  strokeColor: number,
  iconColor: number,
): string {
  return `${LOCKED_CELL_TEXTURE_PREFIX}_${Math.floor(size)}_${stateKey}_${canUnlock ? 1 : 0}_${textureKeyColorPart(bgColor)}_${textureKeyColorPart(strokeColor)}_${textureKeyColorPart(iconColor)}`;
}

function createOffscreen2D(
  width: number,
  height: number,
): { canvas: CanvasImageSource; ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D } | null {
  const g = globalThis as typeof globalThis & {
    OffscreenCanvas?: new (width: number, height: number) => OffscreenCanvas;
    document?: Document;
  };
  if (typeof g.OffscreenCanvas === "function") {
    const canvas = new g.OffscreenCanvas(width, height);
    const ctx = canvas.getContext("2d");
    return ctx ? { canvas, ctx } : null;
  }
  const canvas = g.document?.createElement("canvas");
  if (!canvas) return null;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  return ctx ? { canvas, ctx } : null;
}

function drawFrame(
  ctx: CanvasRenderingContext2D,
  frame: Phaser.Textures.Frame,
  x: number,
  y: number,
  width: number,
  height: number,
): boolean {
  const image = frame.source.image as CanvasImageSource | undefined;
  if (!image) return false;
  ctx.drawImage(image, frame.cutX, frame.cutY, frame.cutWidth, frame.cutHeight, x, y, width, height);
  return true;
}

function drawTintedFrame(
  ctx: CanvasRenderingContext2D,
  frame: Phaser.Textures.Frame,
  x: number,
  y: number,
  width: number,
  height: number,
  tint: number,
  alpha: number,
): boolean {
  const image = frame.source.image as CanvasImageSource | undefined;
  if (!image) return false;
  const offscreen = createOffscreen2D(width, height);
  if (!offscreen) {
    ctx.save();
    ctx.globalAlpha = alpha;
    const ok = drawFrame(ctx, frame, x, y, width, height);
    ctx.restore();
    return ok;
  }
  offscreen.ctx.clearRect(0, 0, width, height);
  offscreen.ctx.drawImage(image, frame.cutX, frame.cutY, frame.cutWidth, frame.cutHeight, 0, 0, width, height);
  offscreen.ctx.globalCompositeOperation = "source-in";
  offscreen.ctx.fillStyle = colorToCss(tint);
  offscreen.ctx.fillRect(0, 0, width, height);
  offscreen.ctx.globalCompositeOperation = "source-over";

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(offscreen.canvas, x, y, width, height);
  ctx.restore();
  return true;
}

function textureKeyColorPart(value: number): string {
  return Math.max(0, Math.floor(value)).toString(16).padStart(6, "0");
}

function colorToCss(value: number): string {
  return `#${textureKeyColorPart(value).slice(-6)}`;
}
